import React, { createContext, useState, useContext, useEffect } from 'react';
import { UserContext } from '../../BasicComponents/UserContext';
import { resetSession, startSession } from '../../utils/sessionReset';

const NextGenAuthContext = createContext(null);

/**
 * NextGen Builder accounts (nextgen_builders table) and the main learner
 * accounts (users table) are separate identity systems on the backend —
 * builder auth is a plain x-builder-id header, learner-flow features
 * (AI Tutor / Elite Mastery / Mock Interview) key their subscription and
 * usage data off a `user-id` header/value instead.
 *
 * To reuse those learner-flow features without forking their code, every
 * logged-in builder is given a stable, clearly-namespaced pseudo identity
 * (`nextgen_builder_<id>`) that can never collide with a real learner
 * user_id/user_code. It's written to the SAME `user_id` localStorage key
 * those learner components already read (see ExplainPage.jsx,
 * useSubscription.js, tsgAPI.js, etc.) — so reused components work
 * completely unmodified.
 */
export const NEXTGEN_BUILDER_ID_PREFIX = 'nextgen_builder_';
export const nextGenBuilderPseudoUserId = (builderId) => `${NEXTGEN_BUILDER_ID_PREFIX}${builderId}`;

export const NextGenAuthProvider = ({ children }) => {
    const { user, setUser } = useContext(UserContext);
    const [loading, setLoading] = useState(false);

    // Sync user with NextGen Builder needs
    const nextgen_builder = user;

    // Keep the shared `user_id` localStorage key in sync with the current
    // builder on every mount/refresh (not just at login time), so reused
    // learner-flow components always see a valid identity.
    useEffect(() => {
        if (nextgen_builder && nextgen_builder.id != null) {
            localStorage.setItem('user_id', nextGenBuilderPseudoUserId(nextgen_builder.id));
        }
    }, [nextgen_builder]);

    const login = (userData) => {
        // We use the main UserContext for login state
        localStorage.setItem('user', JSON.stringify(userData));
        if (userData && userData.id != null) {
            localStorage.setItem('user_id', nextGenBuilderPseudoUserId(userData.id));
        }
        setUser(userData);
        // A login is a session boundary too. Bumping the epoch here means an
        // incomplete previous logout cannot leak into this session: anything
        // still in flight is discarded, and TeeGee remounts for this builder.
        startSession();
    };

    const logout = async () => {
        // Was a hand-listed subset: it left user_pk, auth_token, refresh_token
        // and the axios x-builder-id default header behind, and never told the
        // server. resetSession does all of it, and broadcasts so TeeGee drops
        // this builder's private state.
        await resetSession({ setUser });
    };

    const updateBuilder = (updatedData) => {
        const newData = { ...user, ...updatedData };
        localStorage.setItem('user', JSON.stringify(newData));
        setUser(newData);
    };

    return (
        <NextGenAuthContext.Provider value={{ 
            nextgen_builder, 
            builder: nextgen_builder, 
            login, 
            logout, 
            updateBuilder,
            loading 
        }}>
            {children}
        </NextGenAuthContext.Provider>
    );
};

export const useNextGenAuth = () => useContext(NextGenAuthContext);
